import type { BackgroundCheck, ScreeningProviderConfiguration } from "@repo/models";
import type { GridList } from "@repo/ui";
import type { AxiosRequestConfig } from "axios";

import type { BackgroundChecksFilter, ConfigurationsFilter } from "../pages/backgroundChecks/types";

import { BaseController } from "./baseController";
import { BaseGridApi } from "./baseGridApi";

export const controller = "BackgroundChecks";

export default class BackgroundChecksApi {
	static Get = BaseController.GetFn(controller);
	static Post = BaseController.PostFn(controller);

	/** Loads all background checks matching filters */
	static getBackgroundChecks(filter: BackgroundChecksFilter, config?: AxiosRequestConfig) {
		return BackgroundChecksApi.Get<GridList<BackgroundCheck>>("GetBackgroundChecks", filter, config);
	}

	static downloadBulkUploadTemplate() {
		return BackgroundChecksApi.Get<Blob>("DownloadBulkUploadTemplate", {}, { responseType: "blob" });
	}

	/** Process bulk import file */
	static processBulkImportFile(data: FormData) {
		return BackgroundChecksApi.Post<{ importLog: string; errorLog?: string }>("ProcessBulkImportFile", {}, data);
	}

	/** Loads screening providers for filtering */
	static getScreeningProviders(isIntegrated?: boolean) {
		return BackgroundChecksApi.Get<{ ScreeningProviderId: number; Name: string }[]>("GetScreeningProviders", {
			isIntegrated,
		});
	}

	/** Loads screening products */
	static getScreeningProducts() {
		return BackgroundChecksApi.Get<{ ScreeningProductId: number; Name: string }[]>("GetScreeningProducts");
	}

	/** Loads configurations for grid */
	static getConfigurations(filter: ConfigurationsFilter, config?: AxiosRequestConfig) {
		return BackgroundChecksApi.Get<GridList<ScreeningProviderConfiguration>>("GetConfigurations", filter, config);
	}

	/** Saves configuration */
	static saveConfiguration(data: Partial<ScreeningProviderConfiguration>) {
		return BackgroundChecksApi.Post<void>("SaveConfiguration", {}, data);
	}

	/** Tests connection */
	static testConfiguration(data: Partial<ScreeningProviderConfiguration>) {
		return BackgroundChecksApi.Post<{ success: boolean; message: string }>("TestConnection", {}, data);
	}

	/** Loads saved grid settings for drug tests */
	static loadGridSettings() {
		return BaseGridApi.loadGridSettings(controller, "DrugTests");
	}

	/** Saves grid settings for drug tests */
	static saveGridSettings(settingsJson: string) {
		return BaseGridApi.saveGridSettings(controller, "DrugTests", settingsJson);
	}

	static getGridColumns() {
		return BaseGridApi.getGridColumns<any>(controller, "BackgroundCheck");
	}
}
